import React, { memo, useMemo, useId, useCallback, useEffect } from "react";
import { Card, CardHeader, CardContent, Grid, Typography } from "@mui/material";
import { LifetimeMetricCard } from "../../components/dashboard/LifetimeMetricCard";
import { LoadingMetricCard } from "../../components/dashboard/LoadingMetricCard";
import { ErrorStateCard } from "../../components/dashboard/ErrorStateCard";
import { useFinancialLifetimeMetrics } from "../../hooks/useFinancialLifetimeMetrics";

interface LifetimeMetrics {
  totalSent: number;
  openRate: number;
  clickRate: number;
  deliveryRate: number;
}

const MailerLifetimeMetrics: React.FC = () => {
  // Hook Integration - Query financial-campaign-tracking DynamoDB table
  // via /financial/tracking-data endpoint for financial report metrics
  const {
    data: financialMetrics,
    isLoading,
    error,
    refetch,
  } = useFinancialLifetimeMetrics();
  const titleId = useId();
  const descriptionId = useId();

  // Memoized refetch callback for error state
  const handleRetry = useCallback(() => {
    if (refetch) {
      console.log("Manually triggering refetch of financial lifetime metrics");
      refetch();
    }
  }, [refetch]);

  // Add a button to manually refetch data for testing
  useEffect(() => {
    // For debugging - adds a temporary refetch button
    const addRefetchButton = () => {
      const existingBtn = document.getElementById("debug-refetch-btn");
      if (existingBtn) return;

      const btn = document.createElement("button");
      btn.id = "debug-refetch-btn";
      btn.textContent = "Refresh Metrics";
      btn.style.position = "fixed";
      btn.style.bottom = "20px";
      btn.style.right = "20px";
      btn.style.zIndex = "9999";
      btn.style.padding = "8px 16px";
      btn.style.backgroundColor = "#2196f3";
      btn.style.color = "white";
      btn.style.border = "none";
      btn.style.borderRadius = "4px";
      btn.style.cursor = "pointer";

      btn.addEventListener("click", () => {
        console.log("Manual refetch triggered");
        refetch();
      });

      document.body.appendChild(btn);
    };

    addRefetchButton();

    return () => {
      const btn = document.getElementById("debug-refetch-btn");
      if (btn) btn.remove();
    };
  }, [refetch]);

  // Data Transformation - Convert financial API response to component format
  const metrics = useMemo((): LifetimeMetrics | null => {
    if (!financialMetrics) {
      return null;
    }

    return {
      totalSent: financialMetrics.totalSent || 0,
      openRate: financialMetrics.openRate || 0,
      clickRate: financialMetrics.clickRate || 0,
      deliveryRate: financialMetrics.deliveryRate || 0,
    };
  }, [financialMetrics]);

  // Pending Data Logic - Show overlay on cards when no data has been sent yet
  const isPendingData = useMemo(() => {
    return metrics ? metrics.totalSent === 0 : false;
  }, [metrics]);

  // Loading and Error State Implementation (Subtask 3.3)
  if (error) {
    return (
      <Card elevation={0} sx={{ height: "100%" }}>
        <CardHeader
          title={
            <Typography id={titleId} variant="h5" component="h2">
              Financial Email Metrics
            </Typography>
          }
          sx={{ pb: 1 }}
        />
        <CardContent>
          <ErrorStateCard
            message="Unable to load financial email metrics. Please try again later."
            onRetry={handleRetry}
            variant="alert"
            severity="error"
            data-testid="mailer-metrics-error"
          />
        </CardContent>
      </Card>
    );
  }

  return (
    <section aria-labelledby={titleId}>
      <Card
        elevation={0}
        sx={{ height: "100%", display: "flex", flexDirection: "column" }}
      >
        <CardHeader
          title={
            <Typography id={titleId} variant="h5" component="h2">
              Financial Email Metrics
            </Typography>
          }
          sx={{ pb: 1 }}
        />
        <CardContent sx={{ flex: 1 }}>
          {isLoading ? (
            <Grid
              container
              spacing={2}
              role="status"
              aria-label="Loading metrics"
            >
              {[...Array(4)].map((_, i) => (
                <Grid item xs={12} sm={6} key={`skeleton-${i}`}>
                  <LoadingMetricCard />
                </Grid>
              ))}
            </Grid>
          ) : metrics ? (
            <Grid
              container
              spacing={2}
              role="region"
              aria-label="Mailer metrics data"
            >
              {/* Total Emails Sent */}
              <Grid
                item
                xs={12}
                sm={6}
                data-testid="mailer-metric-total-emails-sent"
              >
                <LifetimeMetricCard
                  label="Total Emails Sent"
                  value={metrics.totalSent.toLocaleString()}
                  isPending={isPendingData}
                />
              </Grid>

              {/* Open Rate */}
              <Grid item xs={12} sm={6} data-testid="mailer-metric-open-rate">
                <LifetimeMetricCard
                  label="Open Rate"
                  value={(metrics.openRate * 100).toFixed(1)}
                  unit="%"
                  isPending={isPendingData}
                />
              </Grid>

              {/* Click Rate */}
              <Grid item xs={12} sm={6} data-testid="mailer-metric-click-rate">
                <LifetimeMetricCard
                  label="Click Rate"
                  value={(metrics.clickRate * 100).toFixed(1)}
                  unit="%"
                  isPending={isPendingData}
                />
              </Grid>

              {/* Delivery Rate */}
              <Grid
                item
                xs={12}
                sm={6}
                data-testid="mailer-metric-delivery-rate"
              >
                <LifetimeMetricCard
                  label="Delivery Rate"
                  value={(metrics.deliveryRate * 100).toFixed(1)}
                  unit="%"
                  isPending={isPendingData}
                />
              </Grid>
            </Grid>
          ) : null}
        </CardContent>
      </Card>
    </section>
  );
};

export default memo(MailerLifetimeMetrics);
